import { type ReactNode } from 'react';
import type { IReportType } from './analytics.types';
import styles from './ReportCard.module.css';

interface IReportCardProps {
  name: IReportType | 'scissorLiftExport';
  title: string;
  description: string;
  icon: ReactNode;
  isSelected: boolean;
  onSelect: (name: IReportCardProps['name']) => void;
  onSubmit: () => void;
  submitLabel: string;
  submitDisabled: boolean;
  children?: ReactNode;
}

/**
 * Mirrors trusstor's `radio-button.svg` / `radio-button-selected.svg`:
 * empty navy ring when unselected, filled navy circle with a white tick
 * when selected.
 */
function RadioGlyph({ selected }: { selected: boolean }) {
  if (selected) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="8" fill="var(--navy)" />
        <path
          d="M4.5 8.2 7 10.5l4.5-4.8"
          fill="none"
          stroke="white"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7.5" fill="none" stroke="var(--navy)" strokeWidth="1" />
    </svg>
  );
}

export function ReportCard({
  name,
  title,
  description,
  icon,
  isSelected,
  onSelect,
  onSubmit,
  submitLabel,
  submitDisabled,
  children,
}: IReportCardProps) {
  const handleCardClick = () => {
    if (!isSelected) onSelect(name);
  };

  return (
    <section
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      data-entity-id={name}
      onClick={handleCardClick}
      style={{ cursor: isSelected ? 'default' : 'pointer' }}
    >
      <header className={styles.header}>
        <span className={styles.icon}>{icon}</span>
        <h3 className={styles.title}>{title}</h3>
        <RadioGlyph selected={isSelected} />
      </header>

      {!isSelected ? (
        <p className={styles.description}>{description}</p>
      ) : (
        <div className={styles.selectedBody} onClick={(e) => e.stopPropagation()}>
          <div className={styles.filters}>{children}</div>
          <button
            type="button"
            className={styles.submit}
            disabled={submitDisabled}
            onClick={onSubmit}
          >
            {submitLabel}
            <span className={styles.submitArrow} aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </section>
  );
}
